/*
 * The fixed-timestep accumulator: elapsed wall time in, whole ticks out.
 *
 * A port, in spirit and in arithmetic, of `crates/sim-cli/src/timestep.rs` — the
 * headless host and the browser host must agree about what a tick is worth, or
 * the replay recorded in one will not describe the run in the other. The two
 * properties worth restating from that file, because they are why this is
 * integer arithmetic and not `accumulator += dt`:
 *
 * - **No floats in the accumulator.** A tick rate that does not divide a second
 *   evenly leaves a remainder, and a float accumulator turns that remainder into
 *   drift nobody can reproduce. The accumulator below counts in nanosecond-hertz
 *   units, where one tick costs exactly one billion of them, so the remainder is
 *   carried exactly. JavaScript's `number` is exact for integers below 2^53 and
 *   every quantity here stays far below that (a full second of credit at 30 Hz
 *   is 3e10), so "integer" is a real claim, not a hopeful one.
 * - **A max-steps clamp.** Without one, a host that falls behind asks for more
 *   ticks than it can run, falls further behind, and spirals. With one, the
 *   simulation visibly slows down instead — the backlog past the clamp is
 *   abandoned, not owed (Gaffer's fix-your-timestep; rust guide §10.2).
 *
 * Time arrives as a parameter. The clock is read in exactly one place in this
 * client, `host/loop.ts`, which is what lets `dev/selfcheck.ts` drive an hour of
 * frames instantly and with no flakiness.
 */

/**
 * Simulation ticks per wall-clock second.
 *
 * The one place this number exists in the client. Thirty rather than sixty: the
 * sim's work per tick is chunk synthesis and connectivity, not animation, and
 * halving the tick rate doubles the headroom before the clamp engages while the
 * renderer keeps interpolating at whatever the display runs at. It is also a
 * clean divisor of both 60 and 120 Hz displays, so a step lands on a frame
 * boundary rather than beating against it.
 *
 * PROVISIONAL: nothing in the sim depends on the host's tick rate yet — the
 * calendar's ticks-per-day is `sim-core`'s business — so this is a host-side
 * pacing choice, revisited when Phase 1's perf numbers exist (roadmap, Gate A).
 */
export const TICK_HZ = 30;

/** Seconds of simulated time in one tick. Derived from {@link TICK_HZ}, never typed out. */
export const TICK_SECONDS = 1 / TICK_HZ;

/**
 * The most ticks one {@link Accumulator.advance} call may run.
 *
 * Matches `MAX_STEPS_PER_FRAME` in `crates/sim-cli/src/host.rs`, and is
 * provisional for the same reason it is there: the guide requires the clamp and
 * names no number. Eight ticks is 267 ms of simulated time at 30 Hz — enough to
 * absorb a garbage-collection pause or a tab switch, small enough that a machine
 * which genuinely cannot keep up shows a slow world instead of a frozen one.
 */
export const MAX_STEPS_PER_FRAME = 8;

/** Nanoseconds in one second: the unit the accumulator's arithmetic is scaled by. */
const NANOS_PER_SECOND = 1_000_000_000;

/** Nanoseconds in one millisecond, which is the unit browsers report time in. */
const NANOS_PER_MILLI = 1_000_000;

/** What one {@link Accumulator.advance} call did. */
export interface Advance {
  /** Ticks run during the call. */
  readonly steps: number;

  /**
   * Ticks that were due and were abandoned by the clamp.
   *
   * Non-zero means the host cannot keep up at this tick rate. The simulation has
   * slowed down relative to the wall clock and — this is the part worth
   * reporting — has *not* accrued a debt it will try to pay back later.
   */
  readonly droppedSteps: number;
}

/**
 * Turns a stream of frame durations into a whole number of simulation ticks.
 *
 * One instance drives one loop. The host samples its clock, hands the elapsed
 * milliseconds to {@link Accumulator.advance}, and the accumulator calls back
 * once per tick that is due — up to its clamp.
 */
export class Accumulator {
  /** Ticks the host wants per wall-clock second. */
  private readonly ticksPerSecond: number;

  /** The most ticks one {@link Accumulator.advance} call may run. */
  private readonly maxSteps: number;

  /**
   * Unspent time in nanosecond-hertz units: elapsed nanoseconds multiplied by the
   * tick rate. One tick costs exactly `NANOS_PER_SECOND` of them, which is what
   * makes the conversion exact at every rate.
   *
   * Invariant, maintained by `advance` and relied on by `alpha`: between calls
   * this is always less than `NANOS_PER_SECOND`, because a whole tick's worth is
   * either run or dropped before `advance` returns.
   */
  private pending = 0;

  constructor(ticksPerSecond: number = TICK_HZ, maxSteps: number = MAX_STEPS_PER_FRAME) {
    if (!Number.isInteger(ticksPerSecond) || ticksPerSecond <= 0) {
      throw new Error(`tick rate must be a positive integer, got ${ticksPerSecond}`);
    }

    if (!Number.isInteger(maxSteps) || maxSteps <= 0) {
      throw new Error(`max steps must be a positive integer, got ${maxSteps}`);
    }

    this.ticksPerSecond = ticksPerSecond;
    this.maxSteps = maxSteps;
  }

  /**
   * The fraction of the way to the next tick, in 0..1.
   *
   * The renderer interpolates by this: a frame drawn between two ticks shows the
   * world where it would be partway between them, which is what stops a 30 Hz
   * simulation from looking like a 30 Hz game.
   */
  get alpha(): number {
    return this.pending / NANOS_PER_SECOND;
  }

  /**
   * Credits `elapsedMillis` of wall time and runs `step` once per tick that is due.
   *
   * A frame shorter than one tick period runs nothing and keeps the time — the
   * remainder is carried, never rounded away. The callback receives nothing but
   * the fixed step length: what to do with a tick is the caller's business, and
   * the accumulator's only job is deciding how many of them the clock has earned.
   */
  advance(elapsedMillis: number, step: (stepSeconds: number) => void): Advance {
    // A clock that goes backwards, or a first frame with no previous timestamp,
    // credits nothing rather than throwing. `performance.now()` is monotonic
    // within a document, but a restored page can hand us a timestamp from a
    // different time origin, and that must not run a negative number of ticks.
    const millis = Number.isFinite(elapsedMillis) && elapsedMillis > 0 ? elapsedMillis : 0;

    // Rounded to whole nanoseconds so the accumulator stays integral. The
    // discarded half-nanosecond per frame is thirty nanoseconds per second at 60
    // fps — six orders of magnitude below the precision the browser reports in
    // the first place, and bounded rather than accumulating.
    const nanos = Math.round(millis * NANOS_PER_MILLI);

    // Saturating, like the Rust original: an absurd elapsed time must clamp to an
    // enormous backlog that the max-steps clamp then drops, never wrap into a
    // small number of ticks.
    const credit = Math.min(nanos * this.ticksPerSecond, Number.MAX_SAFE_INTEGER);
    this.pending = Math.min(this.pending + credit, Number.MAX_SAFE_INTEGER);

    const due = Math.floor(this.pending / NANOS_PER_SECOND);
    const steps = Math.min(due, this.maxSteps);

    if (due > this.maxSteps) {
      // The clamp engaged. The backlog is abandoned wholesale — including the
      // sub-tick remainder, which is at most one tick of an already late frame
      // and not worth carrying into a loop that is behind.
      this.pending = 0;
    } else {
      this.pending -= steps * NANOS_PER_SECOND;
    }

    const stepSeconds = 1 / this.ticksPerSecond;

    for (let i = 0; i < steps; i += 1) {
      step(stepSeconds);
    }

    return { steps, droppedSteps: due - steps };
  }
}
